import React, { useState } from 'react'
import { Box, Stack, Typography, Tooltip, useTheme, Collapse } from '@mui/material'
import { SepTitle } from 'components/global/Title/TitleStyles'
import { useSitesSelected } from '../../TreeProvider'

export const Counter = ({ nb }) => (
  <Stack
    direction={'row'}
    sx={{ border: '1px solid #EFEFEF', borderRadius: 4, paddingLeft: 1, paddingRight: 1, color: '#7A8096' }}>
    <Typography variant="caption" sx={{ fontWeight: 600 }}>
      {nb}
    </Typography>
  </Stack>
)

const Sites = ({ sites, isLast = false }) => {
  return (
    <Box
      sx={{
        overflow: 'visible'
      }}>
      {sites
        .slice()
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((site, index) => {
          const isLastSite = index === sites.length - 1
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: '8px',
                paddingLeft: '20px',
                position: 'relative',
                minHeight: '24px',
                paddingY: '2px',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: isLastSite ? '50%' : 0,
                  width: '1px',
                  backgroundColor: '#E5E7EB'
                },
                ...(isLastSite && {
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    width: '14px',
                    height: '1px',
                    backgroundColor: '#E5E7EB'
                  }
                })
              }}
              key={site.id}>
              <Typography
                color={'#7A8096'}
                fontSize={'12px'}
                variant={'caption'}
                sx={{
                  marginLeft: '8px',
                  wordBreak: 'break-word',
                  lineHeight: 1.4
                }}>
                {site.label}
              </Typography>
            </Box>
          )
        })}
    </Box>
  )
}

const Account = ({ account, expand, hasAccountParentBadge = false, hasError = false, accountParentTooltip }) => {
  const [expanded, setExpanded] = useState(() => {
    return account.children.length === 1 || expand
  })

  const theme = useTheme()

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        mt={1}
        gap={1}
        sx={{
          cursor: account.children.length > 1 ? 'pointer' : 'default'
        }}
        onClick={() => {
          if (account.children.length > 1) {
            setExpanded(!expanded)
          }
        }}>
        <Box
          sx={{
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            fontWeight: 500,
            color: hasError ? theme.palette.error.main : expanded ? '#3B82F6' : '#5D5F70',
            transition: 'color 0.2s ease-in-out'
          }}>
          <Box
            sx={{
              width: '16px',
              display: 'flex',
              justifyContent: 'center',
              transition: 'transform 0.2s ease-in-out'
            }}>
            <i className={`fa-regular fa-angle-${!expanded ? 'right' : 'down'}`} />
          </Box>
          <Typography variant={'caption'}>{account.name}</Typography>
          {hasAccountParentBadge &&
            (accountParentTooltip ? (
              <Tooltip
                title={
                  typeof accountParentTooltip === 'function' ? accountParentTooltip(account) : accountParentTooltip
                }
                placement={'top'}
                arrow
                sx={{ maxWidth: '40px' }}>
                <i
                  className={'fa-regular fa-badge-check'}
                  style={{ color: theme.palette.primary.main, fontSize: 16 }}
                />
              </Tooltip>
            ) : (
              <i className={'fa-regular fa-badge-check'} style={{ color: theme.palette.primary.main, fontSize: 16 }} />
            ))}
        </Box>
        <SepTitle hasError={hasError} />
        <Counter nb={account.children.length} />
      </Stack>

      <Collapse
        in={expanded}
        timeout={300}
        sx={{
          width: '100%'
        }}>
        <Box>
          <Sites sites={account.children} />
        </Box>
      </Collapse>
    </Box>
  )
}

const Accounts = ({ accounts, hasAccountParentBadge = false, accountParentTooltip }) => {
  const { sites } = useSitesSelected({ selection: accounts })

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: 'auto',
        maxHeight: 'calc(100% - 16px)',
        overflow: 'auto',
        width: '100%'
      }}>
      {sites && sites.length > 0 ? (
        [...sites]
          .sort((a, b) => {
            if (a.has_error !== b.has_error) {
              return a.has_error ? -1 : 1
            }

            return a.name.localeCompare(b.name)
          })
          .map(account => (
            <Account
              key={account.sf_id}
              hasAccountParentBadge={hasAccountParentBadge && account.nb_children === account.children.length}
              hasError={account.has_error}
              accountParentTooltip={accountParentTooltip}
              account={account}
              expand={false}
            />
          ))
      ) : (
        <Typography variant="body2" sx={{ color: '#7A8096', padding: '8px 0' }}>
          No sites available
        </Typography>
      )}
    </Box>
  )
}

export default Accounts
